import { RAI_SSO_SERVICE_URL } from "../definitions.js"
import { raisso } from "../raisso.js"
import { responseValidation, apiCall } from "../utils.js"
import { DownloadResponse, GenericResponse, ReportsRequest, UpdateDataRequest, UserDetail } from "./service-models.js"
import { downloadResponseSchema, genericResponseSchema, userDetailSchema } from "./utils.js"
import { RaissoError, ServiceResponse, errorFactory } from "../responses.js"
import { R } from '@mobily/ts-belt'
import { z } from "zod"
import objectToFormData from "./json-to-formdata.js"

const BASE_URL = `${RAI_SSO_SERVICE_URL}/user`;

export const USER_DETAILS_URL = `${BASE_URL}/infox`

function error(e: unknown) {
  console.error('[user-controller] Error:', e);
}

export type AppUpdateRequest = {
  firstName: string
  lastName: string
  gender: string
  birthDate: string
  image?: string
}

class UserController {
  private constructor() { /* empty */ }

  private static _instance: UserController;

  public static get instance(): UserController {
    if (!this._instance) {
      this._instance = new UserController()
    }
    return this._instance;
  }

  public async update(data: UpdateDataRequest): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/update`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) }} }
      ));
    }
  }

  public async socialPair(token: string): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/socialpair`;
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'POST', { 'x-auth-token': token })
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async reports(data: ReportsRequest) {
    const url = `${BASE_URL}/reports`
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'POST', {
        'content-type': 'application/json'
      }, JSON.stringify(data))
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) } } }
      ))
    }
  }

  public async deleteEmail() {
    const url = `${BASE_URL}/delete/email`
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST'
      })
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async credentialTokenChange(oldPassword: string, newPassword: string) {
    const url = `${BASE_URL}/credential/token/change`
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST',
        headers: {
          oldPassword,
          newPassword,
        }
      });
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify({ oldPassword, newPassword })}}}
      ))
    }
  }

  public async credentialRecovery(email: string) {
    let url = `${BASE_URL}/credential/recovery`
    const u = new URL(url, window.location.origin);
    u.searchParams.set('email', email);
    url = u.href;
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'POST')
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url } } }
      ))
    }
  }

  public async credentialChange(password: string, tokenChangePassword: string) {
    const url = `${BASE_URL}/credential/change`
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'POST')
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify({ password, tokenChangePassword }) } } }
      ))
    }
  }

  public async appUpdate(data: AppUpdateRequest) {
    let url = `${BASE_URL}/app/update`;
    const body = objectToFormData(data);
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST',
        body
      })
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) } } }
      ))
    }
  }

  public async trackRequest() {
    const url = `${BASE_URL}/track/request`
    try {
      const response = await raisso.callAuthFetch<DownloadResponse>(url, {
        method: 'GET',
      });
      const value = R.map(response, responseValidation(downloadResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async trackExport() {
    const url = `${BASE_URL}/track/export`
    try {
      const response = await raisso.callAuthFetch<DownloadResponse>(url, {
        method: 'GET'
      })
      const value = R.map(response, responseValidation(downloadResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async trackDownload(token: string) {
    const url = `${BASE_URL}/track/download/${encodeURIComponent(token)}`
    try {
      const response = await apiCall(url, 'text', 'GET',)
      return response
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url } } }
      ))
    }
  }

  public async trackDomain(domainApiKey: string) {
    const url = `${BASE_URL}/track/domain/${encodeURIComponent(domainApiKey)}`
    try {
      const response = await raisso.callAuthFetch(url, {
        method: 'GET',
      }, 'text');
      return response;
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async service() {
    const url = `${BASE_URL}/service`
    try {
      const response = await raisso.callAuthFetch<Record<string, boolean>>(url, {
        method: 'GET',
      });
      const validator = z.record(z.boolean())
      const value = R.map(response, responseValidation(validator))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async serviceNl() {
    const url = `${BASE_URL}/service/nl`
    try {
      const response = await raisso.callAuthFetch<Record<string, unknown>>(url, {
        method: 'GET',
      });
      const validator = z.record(z.unknown())
      const value = R.map(response, responseValidation(validator))
      return value;
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async infox() {
    const url = USER_DETAILS_URL
    try {
      const response = await raisso.callAuthFetch<UserDetail>(url, {
        method: 'GET',
      });
      const value = R.map(response, responseValidation(userDetailSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async credentialRequestTokenChange() {
    const url = `${BASE_URL}/credential/request/token/change`
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'GET',
      })
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async credentialRequestChange(email: string) {
    const url = `${BASE_URL}/credential/request/change`
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'GET', { email })
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(email) } } }
      ))
    }
  }

  public async activation(t: string) {
    let url = `${BASE_URL}/activation`
    const u = new URL(url, window.location.origin);
    const searchParams = new URLSearchParams(t);
    if (searchParams.has("t")) {
      u.search = searchParams.toString();
    } else {
      u.searchParams.set('t', t);
    }
    url = u.href;
    try {
      const response = await apiCall<GenericResponse>(url, 'json', 'GET')
      const value = R.map(response, responseValidation(genericResponseSchema))
      return value
    } catch (e) {
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url }} }
      ))
    }
  }
}

export default UserController;
